import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { TrackingApi } from 'src/api';
import { Icon, Tooltip } from 'src/components';
import { useUserContext } from 'src/context/user';
import theme from 'src/utils/theme';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import * as S from './styles';

const msgs = defineMessages({
  likeVideo: {
    id: 'learning_session_like_video',
    defaultMessage: 'Like this video',
  },
});

const SPARKLE_COUNT = 6;
const CELEBRATION_MS = 700;

export interface LikeVideoButtonProps {
  liked: boolean;
  onToggle: () => void;
  languagePairId?: number;
  learnableId?: number;
  videoSrc?: string | null;
  className?: string;
  trackingData?: Record<string, string>;
}

export const LikeVideoButton: FC<LikeVideoButtonProps> = ({
  liked,
  onToggle,
  languagePairId,
  learnableId,
  videoSrc,
  className,
  trackingData,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const label = intl.formatMessage(msgs.likeVideo);
  const wasLikedRef = useRef(liked);
  const [isCelebrating, setIsCelebrating] = useState(false);

  useEffect(() => {
    if (liked && !wasLikedRef.current) {
      setIsCelebrating(true);
    }
    if (!liked) {
      setIsCelebrating(false);
    }
    wasLikedRef.current = liked;
  }, [liked]);

  useEffect(() => {
    if (!isCelebrating) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsCelebrating(false);
    }, CELEBRATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isCelebrating]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const willBeLiked = !liked;
    const wordlistId = getActiveWordlistId();
    const isProUser = !!user?.isPro;

    void TrackingApi.trackUserAction(
      'lwl_like_button_clicked',
      'learning_session',
      languagePairId ?? null,
      isProUser,
      {
        ...trackingData,
        is_pro_user: String(isProUser),
        liked: String(willBeLiked),
        ...(wordlistId && { wordlist_id: wordlistId }),
        ...(videoSrc && { video_src: videoSrc }),
        ...(learnableId != null && { learnable_id: String(learnableId) }),
      },
    );

    onToggle();
  };

  return (
    <Tooltip placement="bottom" tooltip={label} disabled={isMobile}>
      <S.Button
        type="button"
        className={className}
        aria-label={label}
        $liked={liked}
        onClick={handleClick}
        data-testid="like-video-button"
      >
        <S.HeartIconWrap $celebrating={isCelebrating} data-testid="like-video-heart">
          <Icon name={liked ? 'heartFilled' : 'heartOutline'} />
        </S.HeartIconWrap>
        {isCelebrating &&
          Array.from({ length: SPARKLE_COUNT }, (_, index) => (
            <S.Sparkle
              key={index}
              $index={index}
              $count={SPARKLE_COUNT}
              data-testid="like-video-sparkle"
            />
          ))}
      </S.Button>
    </Tooltip>
  );
};
